import { useEffect, useMemo, useState } from 'react';
import { Container } from 'react-bootstrap';
import { useFetch } from '../../hooks/useFetch';
import { useDocumentMeta } from '../../hooks/useDocumentMeta';
import { useAuthContext } from '../../context/AuthProvider';
import { can } from '../../utils/access';
import { fetchUsersService, updateUserRolesService, createUserService } from '../../services/usersService';
import UserForm from '../../components/components-admin/UserForm';
import RoleBadges from '../../components/components-admin/RoleBadges';
import './admin.css';

const ROLES = [
    { value: '', label: 'Todos los roles' },
    { value: 'client', label: 'Clientes' },
    { value: 'model', label: 'Modelos' },
    { value: 'admin', label: 'Administradores' },
    { value: 'root', label: 'Root' },
];

// `hour12` explícito: con el formato del locale ("06:17 p. m.") la celda se
// partía en tres líneas y estiraba toda la fila.
const fecha = (valor) => {
    if (!valor) return '—';
    const d = new Date(valor);
    return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}`;
};

/**
 * Las personas de la plataforma.
 *
 * Sustituye a la pantalla "Clientes", que consultaba una ruta fija de clientes
 * y por tanto no enseñaba ni modelos ni administradores, ni permitía saber qué
 * rol tenía nadie. Quién ve qué lo decide el servidor con los permisos:
 * `view_all_users` ve a todo el mundo y `view_clients` solo a los clientes, así
 * que el selector de rol solo se ofrece a quien puede usarlo.
 */
export default function UsersPage() {
    useDocumentMeta({ title: 'Usuarios', noIndex: true });

    const { user } = useAuthContext();
    const puedeVerTodos = can(user, 'view_all_users');
    const puedeGestionar = can(user, 'manage_users');

    const [role, setRole] = useState('');
    const [search, setSearch] = useState('');
    const [debounced, setDebounced] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [status, setStatus] = useState({ type: '', message: '' });

    // Debounce del buscador: evita una petición por cada tecla.
    useEffect(() => {
        const timer = setTimeout(() => setDebounced(search), 400);
        return () => clearTimeout(timer);
    }, [search]);

    const params = useMemo(
        () => ({ role: puedeVerTodos ? role : '', search: debounced, per_page: 30 }),
        [puedeVerTodos, role, debounced]
    );

    const { data: users, error, loading, setData, refresh } = useFetch(fetchUsersService, { params });

    const cambiarRol = async (fila, nuevoRol) => {
        setStatus({ type: '', message: '' });
        try {
            const actualizado = await updateUserRolesService(fila.id, [nuevoRol]);
            setData((prev) => prev.map((item) => (item.id === fila.id ? actualizado : item)));
            setStatus({ type: 'success', message: `${actualizado.name} ahora es ${nuevoRol}.` });
        } catch (err) {
            setStatus({ type: 'error', message: err.message });
        }
    };

    const crear = async (datos) => {
        await createUserService(datos);
        setShowForm(false);
        setStatus({ type: 'success', message: `${datos.name} creado como ${datos.role}.` });
        refresh();
    };

    return (
        <div className="admin-container">
            <Container fluid>
                <div className="admin-header">
                    <div>
                        <h2 className="admin-title">Usuarios</h2>
                        <p className="text-muted small mb-0">
                            {puedeVerTodos
                                ? 'Todas las personas registradas en la plataforma'
                                : 'Clientes registrados en la plataforma'}
                        </p>
                    </div>
                    {puedeGestionar && (
                        <button className="btn-create-model" onClick={() => setShowForm(true)}>
                            <i className="fa-solid fa-user-plus" aria-hidden="true"></i>
                            <span>Nuevo usuario</span>
                        </button>
                    )}
                </div>

                <div className="admin-filters">
                    <div className="admin-search">
                        <i className="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
                        <input
                            type="text"
                            placeholder="Buscar por nombre o correo..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            aria-label="Buscar usuarios"
                        />
                    </div>

                    {/* Sin `view_all_users` el servidor impone el filtro de
                        clientes, así que ofrecer el selector solo prometería
                        algo que la respuesta no va a cumplir. */}
                    {puedeVerTodos && (
                        <select value={role} onChange={(e) => setRole(e.target.value)} aria-label="Filtrar por rol">
                            {ROLES.map((item) => (
                                <option key={item.value} value={item.value}>{item.label}</option>
                            ))}
                        </select>
                    )}
                </div>

                {status.message && (
                    <div className={`alert ${status.type === 'success' ? 'alert-success' : 'alert-danger'} py-2 small`}>
                        {status.message}
                    </div>
                )}

                {error && <div className="alert alert-danger">{error}</div>}

                {loading && !users && <p className="text-muted text-center py-5">Cargando usuarios...</p>}

                {users?.length === 0 && !loading && (
                    <div className="table-empty">
                        <i className="fa-solid fa-users-slash" aria-hidden="true"></i>
                        <p>No hay usuarios que coincidan con la búsqueda.</p>
                    </div>
                )}

                {users?.length > 0 && (
                    <div className="custom-table-container">
                        <table className="table table-striped table-bordered table-hover custom-table text-center">
                            <thead>
                                <tr>
                                    <th>Nombre</th>
                                    <th>Email</th>
                                    <th>Rol</th>
                                    <th>Tokens</th>
                                    <th>Registro</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((fila) => (
                                    <tr key={fila.id}>
                                        <td data-label="Nombre" className="fw-bold text-white">{fila.name}</td>
                                        <td data-label="Email" className="text-white cell-email">{fila.email}</td>
                                        <td data-label="Rol">
                                            {puedeGestionar && fila.id !== user?.id ? (
                                                <select
                                                    className="role-select"
                                                    value={fila.roles?.[0] ?? ''}
                                                    onChange={(e) => cambiarRol(fila, e.target.value)}
                                                    aria-label={`Rol de ${fila.name}`}
                                                >
                                                    {ROLES.filter((item) => item.value).map((item) => (
                                                        <option key={item.value} value={item.value}>{item.label}</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <RoleBadges roles={fila.roles} />
                                            )}
                                        </td>
                                        <td data-label="Tokens" className="text-muted">{fila.tokens}</td>
                                        <td data-label="Registro" className="text-muted">{fecha(fila.created_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {puedeGestionar && (
                    <UserForm show={showForm} onHide={() => setShowForm(false)} onSubmit={crear} />
                )}
            </Container>
        </div>
    );
}
